
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function setInfoBannerContent(container: HTMLDivElement, html: string): void {
    // Clear existing content
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }

    // Use a temporary wrapper to parse the HTML string
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Ensure all images have appropriate accessibility attributes
    const images = temp.querySelectorAll('img');
    images.forEach((img: HTMLImageElement, index: number) => {
        if (!img.hasAttribute('alt')) {
            // Treat images in the banner as decorative by default
            img.setAttribute('alt', '');
        }
        if (!img.hasAttribute('loading')) {
            img.setAttribute('loading', 'lazy');
        }
    });

    // Move parsed nodes into the real container
    while (temp.firstChild) {
        container.appendChild(temp.firstChild);
    }
}

// Dictionary of translations
const TEXTS: Record<string, {
    placeholder: string;
    cta: string;
    termsTitle: string;
    termsText: string;
    accept: string;
    cancel: string;
    voiceSupportMessage: string;
}> = {
    es: {
        placeholder: "Escribe aquí para interactuar por texto o dale al icono del teléfono arriba para interactuar por voz.",
        cta: "Habla con nuestra IA",
        termsTitle: "Términos y condiciones",
        termsText: "Al hacer clic en «Aceptar», y cada vez que interactúe con este agente de IA, doy mi consentimiento para la grabación, el almacenamiento y la compartición de mis comunicaciones con proveedores de servicios externos, y conforme a lo descrito en la Política de Privacidad. Si no desea que sus conversaciones sean grabadas, por favor absténgase de utilizar este servicio.",
        accept: "Aceptar",
        cancel: "Cancelar",
        voiceSupportMessage: 'Háblame en <img src="https://flagcdn.com/es.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/gb.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/de.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;">'
    },
    en: {
        placeholder: "Type here to interact via text or click the phone icon above to interact via voice.",
        cta: "Talk with our AI",
        termsTitle: "Terms and Conditions",
        termsText: "By clicking \"Accept\", and each time you interact with this AI agent, I consent to the recording, storage and sharing of my communications with external service providers, as described in the Privacy Policy. If you do not wish your conversations to be recorded, please refrain from using this service.",
        accept: "Accept",
        cancel: "Cancel",
        voiceSupportMessage: 'Speak to me in <img src="https://flagcdn.com/es.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/gb.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/de.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;">'
    },
    de: {
        placeholder: "Hier tippen, um per Text zu interagieren, oder das Telefonsymbol oben für Sprachinteraktion nutzen.",
        cta: "Mit unserer KI sprechen",
        termsTitle: "Allgemeine Geschäftsbedingungen",
        termsText: "Durch Klicken auf «Akzeptieren» und bei jeder Interaktion mit diesem KI-Agenten stimme ich der Aufzeichnung, Speicherung und Weitergabe meiner Kommunikation an externe Dienstleister zu, wie in der Datenschutzrichtlinie beschrieben. Wenn Sie nicht möchten, dass Ihre Gespräche aufgezeichnet werden, unterlassen Sie bitte die Nutzung dieses Dienstes.",
        accept: "Akzeptieren",
        cancel: "Abbrechen",
        voiceSupportMessage: 'Sprich mit mir auf <img src="https://flagcdn.com/es.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/gb.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/de.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;">'
    },
    ca: {
        placeholder: "Escriu aquí per interactuar per text o fes clic a la icona del telèfon a dalt per interactuar per veu.",
        cta: "Parla amb la nostra IA",
        termsTitle: "Termes i condicions",
        termsText: "En fer clic a «Acceptar», i cada vegada que interactuï amb aquest agent d'IA, dono el meu consentiment per a l'enregistrament, l'emmagatzematge i la compartició de les meves comunicacions amb proveïdors de serveis externs, tal com es descriu a la Política de Privacitat. Si no desitja que les seves converses siguin gravades, si us plau abstingui's d'utilitzar aquest servei.",
        accept: "Acceptar",
        cancel: "Cancel·lar",
        voiceSupportMessage: 'Parla\'m en <img src="https://flagcdn.com/es.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/gb.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;"> <img src="https://flagcdn.com/de.svg" width="24" style="vertical-align: middle; margin: 0 3px; display: inline-block;">'
    }
};

export const ElevenLabsLocalizer = () => {
    const { i18n } = useTranslation();
    const observerRef = useRef<MutationObserver | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const lang = (i18n.language || 'es').split('-')[0];
        const texts = TEXTS[lang] || TEXTS['es'];
        let isUpdating = false;

        const updateTexts = () => {
            if (isUpdating) return;
            isUpdating = true;

            try {
            const convai = document.querySelector('elevenlabs-convai');
            if (!convai || !convai.shadowRoot) return;

            // 1. Placeholder
            const textarea = convai.shadowRoot.querySelector('textarea');
            if (textarea && textarea.getAttribute('placeholder') !== texts.placeholder) {
                textarea.setAttribute('placeholder', texts.placeholder);
            }

            // 1b. Remove branding (Powered by ElevenLabs / ElevenAgents)
            const brandingPatterns = ['powered by', 'elevenlabs', 'elevenagents', 'eleven agents', 'eleven labs', 'elevenlabs.io'];
            const brandingEls = convai.shadowRoot.querySelectorAll('a, span, div, p, small, footer');
            brandingEls.forEach(el => {
                const text = (el.textContent || '').toLowerCase().trim();
                if (text && el.children.length <= 1 && brandingPatterns.some(p => text.includes(p))) {
                    const htmlEl = el as HTMLElement;
                    htmlEl.style.display = 'none';
                    htmlEl.style.visibility = 'hidden';
                    htmlEl.style.height = '0';
                    htmlEl.style.overflow = 'hidden';
                    htmlEl.style.opacity = '0';
                    htmlEl.style.pointerEvents = 'none';
                }
            });

            // 2. CTA Text
            const allCtaVariants = Object.values(TEXTS).map(t => t.cta);
            // Include original Spanish just in case
            if (!allCtaVariants.includes("Habla con la IA")) allCtaVariants.push("Habla con la IA");

            const spans = convai.shadowRoot.querySelectorAll('span');
            spans.forEach(span => {
                const currentText = span.textContent?.trim();
                if (currentText && allCtaVariants.includes(currentText)) {
                    if (currentText !== texts.cta) {
                        span.textContent = texts.cta;
                    }
                }
            });

            // 3. Terms and Conditions title (h4)
            const allTermsTitleVariants = Object.values(TEXTS).map(t => t.termsTitle);
            allTermsTitleVariants.push("Términos y condiciones"); // Original fallback

            const h4Elements = convai.shadowRoot.querySelectorAll('h4');
            h4Elements.forEach(h4 => {
                const currentText = h4.textContent?.trim();
                if (currentText && allTermsTitleVariants.includes(currentText)) {
                    if (currentText !== texts.termsTitle) {
                        h4.textContent = texts.termsTitle;
                    }
                }
            });

            // 4. Terms and Conditions text (p inside .terms)
            const allTermsTextVariants = Object.values(TEXTS).map(t => t.termsText);
            allTermsTextVariants.push("Al hacer clic en «Aceptar», y cada vez que interactúe con este agente de IA, doy mi consentimiento para la grabación, el almacenamiento y la compartición de mis comunicaciones con proveedores de servicios externos, y conforme a lo descrito en la Política de Privacidad. Si no desea que sus conversaciones sean grabadas, por favor absténgase de utilizar este servicio.");

            const termsDiv = convai.shadowRoot.querySelector('.terms');
            if (termsDiv) {
                const pElement = termsDiv.querySelector('p');
                if (pElement) {
                    const currentText = pElement.textContent?.trim();
                    if (currentText && allTermsTextVariants.some(variant => currentText.includes(variant.substring(0, 50)))) {
                        if (currentText !== texts.termsText) {
                            pElement.textContent = texts.termsText;
                        }
                    }
                }
            }

            // 5. Accept and Cancel buttons
            const allAcceptVariants = Object.values(TEXTS).map(t => t.accept);
            allAcceptVariants.push("Accept"); // Original English
            const allCancelVariants = Object.values(TEXTS).map(t => t.cancel);
            allCancelVariants.push("Cancel"); // Original English

            const buttons = convai.shadowRoot.querySelectorAll('button');
            buttons.forEach(button => {
                const buttonSpan = button.querySelector('span');
                if (buttonSpan) {
                    const currentText = buttonSpan.textContent?.trim();
                    if (currentText && allAcceptVariants.includes(currentText)) {
                        if (currentText !== texts.accept) {
                            buttonSpan.textContent = texts.accept;
                        }
                    } else if (currentText && allCancelVariants.includes(currentText)) {
                        if (currentText !== texts.cancel) {
                            buttonSpan.textContent = texts.cancel;
                        }
                    }
                }
            });

            // 6. Voice support info banner between phone icon and chat input
            const INFO_ID = 'colbai-voice-support-info';
            const existingInfo = convai.shadowRoot.querySelector(`#${INFO_ID}`);

            // Find the sticky bottom area that contains the input
            let stickyBottom = convai.shadowRoot.querySelector('.sheet .sticky.bottom-0') as HTMLElement | null;
            // The actual input wrapper is the last div child inside sticky
            let inputWrapper = stickyBottom?.querySelector('.relative.w-full') as HTMLElement | null;

            // Fallback: if the ElevenLabs internal classes change, try to anchor to the actual text input
            if (!stickyBottom || !inputWrapper) {
                const textInput = convai.shadowRoot.querySelector(
                    'textarea, input[type="text"], input:not([type])'
                ) as HTMLElement | null;

                if (textInput) {
                    // Use the input's immediate parent as the wrapper and its parent as the sticky container
                    inputWrapper = textInput.parentElement as HTMLElement | null;
                    stickyBottom = inputWrapper?.parentElement as HTMLElement | null;
                }
            }

            if (stickyBottom && inputWrapper) {
                if (!existingInfo) {
                    const info = document.createElement('div');
                    info.id = INFO_ID;
                    setInfoBannerContent(info as HTMLDivElement, texts.voiceSupportMessage);
                    info.style.cssText = `
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 4px;
                        background: #e1f5fe;
                        color: #01579b;
                        border: 1px solid #b3e5fc;
                        border-radius: 8px;
                        padding: 10px 14px;
                        margin: 0 12px 8px 12px;
                        font-size: 13px;
                        line-height: 1.4;
                        text-align: center;
                        font-weight: 500;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        pointer-events: auto;
                        width: calc(100% - 24px);
                        box-sizing: border-box;
                    `;
                    // Insert just before the input wrapper
                    stickyBottom.insertBefore(info, inputWrapper);
                } else if (existingInfo.innerHTML !== texts.voiceSupportMessage) {
                    setInfoBannerContent(existingInfo as HTMLDivElement, texts.voiceSupportMessage);
                }
            } else if (existingInfo) {
                existingInfo.remove();
            }
            } finally {
                isUpdating = false;
            }
        };

        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        const initObserver = () => {
            const convai = document.querySelector('elevenlabs-convai');
            if (convai && convai.shadowRoot) {
                updateTexts();

                if (!observerRef.current) {
                    observerRef.current = new MutationObserver(() => {
                        if (debounceTimer) clearTimeout(debounceTimer);
                        debounceTimer = setTimeout(updateTexts, 150);
                    });

                    observerRef.current.observe(convai.shadowRoot, {
                        childList: true,
                        subtree: true,
                        attributes: false,
                        characterData: true
                    });
                }
            }
        };

        // Close widget when clicking outside — use composedPath to handle Shadow DOM correctly
        const handleClickOutside = (e: MouseEvent) => {
            const convai = document.querySelector('elevenlabs-convai');
            if (!convai || !convai.shadowRoot) return;

            const sheet = convai.shadowRoot.querySelector('.sheet') as HTMLElement | null;
            if (!sheet) return;

            // Use composedPath to reliably detect clicks inside Shadow DOM
            const path = e.composedPath();
            if (path.includes(convai)) return;

            // Click was outside the widget — close it
            const closeBtn = convai.shadowRoot.querySelector('.sheet button[aria-label="Close"], .sheet button[class*="close"]') as HTMLButtonElement | null;
            if (closeBtn) {
                closeBtn.click();
                return;
            }
            const launcherBtn = convai.shadowRoot.querySelector('button') as HTMLButtonElement | null;
            if (launcherBtn && !sheet.contains(launcherBtn)) {
                launcherBtn.click();
            }
        };

        // Always keep checking — never stop the interval
        document.addEventListener('click', handleClickOutside);
        intervalRef.current = setInterval(initObserver, 500);
        initObserver();

        return () => {
            document.removeEventListener('click', handleClickOutside);
            if (debounceTimer) clearTimeout(debounceTimer);
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (observerRef.current) observerRef.current.disconnect();
            observerRef.current = null;
        };
    }, [i18n.language]);

    return null;
};
